/**
 * Planning → Nog in te plannen (Joris, 9 sep 2026).
 *
 * Eén scherm met wat nog geen plek in de planning heeft:
 *  A. projecten met draaidagen waarvan montage/animatie/feedback niet (volledig)
 *     is ingepland — met de offerte-uren ernaast en de ruimte tot de deadline.
 *     Uitklappen toont de echte planning van het project (dezelfde takenrijen
 *     als Planner To-Do) plus wie wat heeft;
 *  B. projecten met taken zonder eigenaar, mét hun planning erbij;
 *  C. de deadline-check: welke deliverables hebben een signaal — ter plekke aan
 *     te passen, met ongedaan maken.
 *
 * Alle rekenregels zitten in de engine (/Api/inplannen/overzicht); dit scherm
 * toont alleen. Besluiten 9 sep: 4 productieve uur per werkdag, ingepland =
 * eigenaar én datum, Strippenkaart buiten beschouwing.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, CircularProgress, Collapse, FormControl, IconButton, InputLabel, MenuItem,
  Select, Stack, Tab, Tabs, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import {
  Refresh as RefreshIcon, OpenInNew as OpenInNewIcon,
  ExpandMore as ExpandMoreIcon, ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import PageHeader from '../../ui-components/PageHeader';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { leesJson } from '../../utils/apiFout';
import { useKlantAfkortingen } from '../../utils/klantAfkortingen';
import { registreerOngedaanSneltoets } from '../../utils/ongedaan';
import { datumKort, disciplinesVoorTab, heeftOpenWerk, uur } from '../../utils/inplannen';
import ProjectPlanning, { Waarschuwing } from './ProjectPlanning';
import OverzichtPerProject from '../PlanningOverzicht/OverzichtPerProject';

const FONT = '"Inter", system-ui, sans-serif';
const NAV_FONT = '"Manrope", system-ui, sans-serif';

const SIGNAAL = {
  geen_deadline: { tekst: 'Geen deadline', kleur: '#b91c1c', bg: '#fee2e2', uitleg: 'Er is montage of animatie geoffreerd, maar de deliverable heeft geen datum.' },
  voor_draaidag: { tekst: 'Vóór de draaidag', kleur: '#b91c1c', bg: '#fee2e2', uitleg: 'De deadline valt eerder dan de laatste vaste draaidag.' },
  verlopen: { tekst: 'Verlopen', kleur: '#b91c1c', bg: '#fee2e2', uitleg: 'De deadline is voorbij en de deliverable staat niet op afgerond.' },
  past_niet: { tekst: 'Past niet', kleur: '#b45309', bg: '#fef3c7', uitleg: 'Meer open uren dan werkdagen × 4 u tussen de laatste draaidag en de deadline.' },
};

const STAAT = {
  niets_gepland: { tekst: 'Niets gepland', kleur: '#b91c1c', bg: '#fee2e2' },
  deels_gepland: { tekst: 'Deels gepland', kleur: '#b45309', bg: '#fef3c7' },
  geen_offerte_uren: { tekst: 'Geen offerte-uren', kleur: '#475569', bg: '#f1f5f9' },
  ingepland: { tekst: 'Ingepland', kleur: '#15803d', bg: '#dcfce7' },
};

const TOESTAND = {
  gepland: { tekst: 'Gepland', kleur: '#15803d', bg: '#dcfce7' },
  toegewezen: { tekst: 'Zonder datum', kleur: '#b45309', bg: '#fef3c7' },
  zonder_eigenaar: { tekst: 'Zonder eigenaar', kleur: '#b91c1c', bg: '#fee2e2' },
};

const DISCIPLINES = [
  { key: 'montage', label: 'Montage', kleur: '#0f766e' },
  { key: 'animatie', label: 'Animatie', kleur: '#7c3aed' },
  { key: 'feedback', label: 'Feedback', kleur: '#2563eb' },
];

const chipSx = (k) => ({ height: 20, fontSize: 11, fontWeight: 700, color: k.kleur, bgcolor: k.bg, fontFamily: FONT });
const dagVan = (v) => (v ? datumKort(String(v).slice(0, 10)) : '');

function Balk({ gepland, offerte, kleur }) {
  const pct = offerte > 0 ? Math.min(100, Math.round((gepland / offerte) * 100)) : 0;
  return (
    <Box sx={{ mt: 0.4, height: 5, borderRadius: 3, bgcolor: '#e2e8f0', position: 'relative', overflow: 'hidden' }}>
      <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${pct}%`, bgcolor: kleur, borderRadius: 3 }} />
    </Box>
  );
}

function Signaal({ code }) {
  const s = SIGNAAL[code];
  if (!s) return null;
  return <Tooltip title={s.uitleg} arrow><Chip size="small" label={s.tekst} sx={chipSx(s)} /></Tooltip>;
}

function ProjectLink({ rij, kortKlant }) {
  const klant = kortKlant ? kortKlant(rij.klant) : rij.klant;
  return (
    <Box>
      <Typography component={RouterLink} to={`/projects/${rij.id}`} onClick={(e) => e.stopPropagation()}
        sx={{ fontFamily: FONT, fontSize: 13.5, fontWeight: 700, color: '#0f172a', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
        {rij.nummer} · {klant}{klant && rij.naam ? ' — ' : ''}{rij.naam}
      </Typography>
      {rij.pl && <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#64748b' }}>PL {rij.pl}</Typography>}
    </Box>
  );
}

/** Wat zit er achter "43 / 96 u"? Voor de tooltip op de discipline-cel. */
function DisciplineTooltip({ rij, discipline, children }) {
  const per = { gepland: [], toegewezen: [], zonder_eigenaar: [] };
  (rij.taken || []).filter((t) => t.discipline === discipline).forEach((t) => per[t.toestand]?.push(t));
  const regel = (t) => `${t.name} · ${uur(Number(t.hours) || 0)} u${t.assignees?.length ? ` · ${t.assignees.join(', ')}` : ''}${t.startDate ? ` · ${dagVan(t.startDate)}` : ''}`;
  const blok = (kop, lijst) => (lijst.length ? (
    <Box key={kop} sx={{ mb: 0.75 }}>
      <Typography sx={{ fontFamily: FONT, fontSize: 11, fontWeight: 700, opacity: 0.8 }}>{kop} ({lijst.length})</Typography>
      {lijst.slice(0, 8).map((t) => <Typography key={t.id} sx={{ fontFamily: FONT, fontSize: 11.5 }}>{regel(t)}</Typography>)}
      {lijst.length > 8 && <Typography sx={{ fontFamily: FONT, fontSize: 11, opacity: 0.7 }}>+{lijst.length - 8} meer</Typography>}
    </Box>
  ) : null);
  const inhoud = [blok('Gepland', per.gepland), blok('Toegewezen, zonder datum', per.toegewezen), blok('Zonder eigenaar', per.zonder_eigenaar)].filter(Boolean);
  return (
    <Tooltip arrow placement="top" title={inhoud.length ? <Box sx={{ p: 0.5 }}>{inhoud}</Box> : 'Nog geen taken voor deze discipline'}>
      <Box sx={{ cursor: 'help' }}>{children}</Box>
    </Tooltip>
  );
}

function BlokA({ rijen, disciplines, kortKlant, onGewijzigd }) {
  const [open, setOpen] = useState({});
  if (!rijen.length) {
    return <Alert severity="success" sx={{ fontFamily: FONT }}>Alles wat gedraaid is of gaat draaien, staat ingepland.</Alert>;
  }
  const kolommen = DISCIPLINES.filter((d) => disciplines.includes(d.key));
  const aantalKolommen = 7 + kolommen.length;
  return (
    <Box sx={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 2, bgcolor: '#fff' }}>
      <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', minWidth: 900, fontFamily: FONT, fontSize: 13,
        '& th': { textAlign: 'left', fontFamily: NAV_FONT, fontSize: 11.5, textTransform: 'uppercase', letterSpacing: '.05em', color: '#64748b', px: 1.5, py: 1, borderBottom: '1px solid #e2e8f0', bgcolor: '#f8fafc', whiteSpace: 'nowrap' },
        '& td': { px: 1.5, py: 1.1, borderBottom: '1px solid #f1f5f9', verticalAlign: 'top' } }}>
        <thead>
          <tr>
            <th style={{ width: 28 }} aria-label="uitklappen" />
            <th>Project</th>
            <th>Draaidagen</th>
            {kolommen.map((k) => <th key={k.key}>{k.label}</th>)}
            <th>Eerste deadline</th>
            <th>Ruimte</th>
            <th>Staat</th>
            <th aria-label="acties" />
          </tr>
        </thead>
        <tbody>
          {rijen.map((rij) => {
            const dd = rij.draaidagen || {};
            const delen = [];
            if (dd.gedraaid) delen.push(`${dd.gedraaid} gedraaid`);
            if (dd.gepland) delen.push(`${dd.gepland} gepland`);
            if (dd.opties) delen.push(`${dd.opties} optie${dd.opties > 1 ? 's' : ''}`);
            const staat = STAAT[rij.staat] || STAAT.deels_gepland;
            const r = rij.ruimte;
            const isOpen = !!open[rij.id];
            const toggle = () => setOpen((p) => ({ ...p, [rij.id]: !p[rij.id] }));
            const eersteDl = (rij.deliverables || []).find((x) => x.deadline === rij.eersteDeadline);
            return (
              <React.Fragment key={rij.id}>
                <tr onClick={toggle} style={{ cursor: 'pointer', background: isOpen ? '#f8fafc' : undefined }}>
                  <td style={{ paddingRight: 0 }}>{isOpen ? <ExpandMoreIcon sx={{ fontSize: 18, color: '#64748b' }} /> : <ChevronRightIcon sx={{ fontSize: 18, color: '#94a3b8' }} />}</td>
                  <td><ProjectLink rij={rij} kortKlant={kortKlant} /></td>
                  <td>
                    <Typography sx={{ fontFamily: FONT, fontSize: 13 }}>{delen.join(' · ') || '–'}</Typography>
                    {dd.laatste && <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#64748b' }}>laatste {datumKort(dd.laatste)}</Typography>}
                  </td>
                  {kolommen.map((k) => {
                    const d = rij.disciplines?.[k.key] || {};
                    const geen = !d.offerte && !d.aantal;
                    const metEigenaar = (d.aantal || 0) - (d.aantalZonderEigenaar || 0);
                    return (
                      <td key={k.key} style={{ minWidth: 130 }}>
                        {geen ? <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: '#94a3b8' }}>–</Typography> : (
                          <DisciplineTooltip rij={rij} discipline={k.key}>
                            <Typography sx={{ fontFamily: FONT, fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>
                              <b>{uur(d.geteld)}</b> / {d.offerte ? `${uur(d.offerte)} u` : '? u'}
                            </Typography>
                            <Balk gepland={d.geteld} offerte={d.offerte} kleur={k.kleur} />
                            <Typography sx={{ fontFamily: FONT, fontSize: 11, color: '#64748b', mt: 0.3 }}>
                              {[
                                metEigenaar > 0 && `${metEigenaar} met eigenaar`,
                                d.verstreken > 0 && `${uur(d.verstreken)} u al voorbij`,
                                d.aantalZonderEigenaar > 0 && `${d.aantalZonderEigenaar} zonder eigenaar`,
                                d.toegewezen > 0 && `${uur(d.toegewezen)} u zonder datum`,
                              ].filter(Boolean).join(' · ') || 'nog geen taken'}
                            </Typography>
                          </DisciplineTooltip>
                        )}
                      </td>
                    );
                  })}
                  <td>
                    {rij.eersteDeadline ? (
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography sx={{ fontFamily: FONT, fontSize: 13 }}>{datumKort(rij.eersteDeadline)}</Typography>
                        {eersteDl?.type && <Chip size="small" label={eersteDl.type} sx={{ height: 18, fontSize: 10.5, fontFamily: FONT }} />}
                      </Stack>
                    ) : <Signaal code="geen_deadline" />}
                  </td>
                  <td>
                    {r ? (
                      <Chip size="small" label={`${r.werkdagen} werkdag${r.werkdagen === 1 ? '' : 'en'} voor ${uur(r.open)} u`}
                        sx={{ height: 20, fontSize: 11.5, fontWeight: 600, fontFamily: FONT, color: r.past ? '#15803d' : '#b91c1c', bgcolor: r.past ? '#dcfce7' : '#fee2e2' }} />
                    ) : <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: '#94a3b8' }}>onbekend</Typography>}
                  </td>
                  <td>
                    <Stack spacing={0.5} alignItems="flex-start">
                      <Chip size="small" label={staat.tekst} sx={chipSx(staat)} />
                      {(rij.waarschuwingen || []).map((w) => <Waarschuwing key={w} code={w} rij={rij} />)}
                    </Stack>
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                    <Tooltip title="Open in Planner To-Do"><IconButton size="small" component={RouterLink} to="/planner-todo"><OpenInNewIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                  </td>
                </tr>
                {isOpen && (
                  <tr>
                    <td colSpan={aantalKolommen} style={{ background: '#f8fafc', paddingTop: 4, paddingBottom: 14 }}>
                      <ProjectPlanning rij={rij} disciplines={disciplines} onGewijzigd={onGewijzigd} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </Box>
    </Box>
  );
}

function BlokB({ projecten, disciplines, kortKlant, onGewijzigd }) {
  const [dicht, setDicht] = useState({});
  const lijst = projecten.filter((p) => (p.taken || []).some((t) => t.toestand === 'zonder_eigenaar' && disciplines.includes(t.discipline)));
  if (!lijst.length) return <Alert severity="success" sx={{ fontFamily: FONT }}>Geen taken zonder eigenaar.</Alert>;
  return (
    <Stack spacing={1.5}>
      {lijst.map((p) => {
        const zonder = (p.taken || []).filter((t) => t.toestand === 'zonder_eigenaar' && disciplines.includes(t.discipline));
        const urenZonder = zonder.reduce((s, t) => s + (Number(t.hours) || 0), 0);
        const open = !dicht[p.id];
        const urgent = String(p.prio || '').startsWith('Prio 1');
        return (
          <Box key={p.id} sx={{ border: '1px solid #e2e8f0', borderRadius: 2, bgcolor: '#fff', overflow: 'hidden' }}>
            <Box onClick={() => setDicht((d) => ({ ...d, [p.id]: !d[p.id] }))}
              sx={{ px: 1.5, py: 1, bgcolor: '#f8fafc', borderBottom: open ? '1px solid #e2e8f0' : 0, display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer' }}>
              {open ? <ExpandMoreIcon sx={{ fontSize: 18, color: '#64748b' }} /> : <ChevronRightIcon sx={{ fontSize: 18, color: '#94a3b8' }} />}
              <Box sx={{ flex: 1 }}><ProjectLink rij={p} kortKlant={kortKlant} /></Box>
              <Chip size="small" label={p.prio} sx={{ height: 20, fontSize: 11, fontFamily: FONT, fontWeight: 700, bgcolor: urgent ? '#fee2e2' : '#f1f5f9', color: urgent ? '#b91c1c' : '#475569' }} />
              <Chip size="small" label={`${zonder.length} zonder eigenaar · ${uur(urenZonder)} u`} sx={chipSx(TOESTAND.zonder_eigenaar)} />
              {p.eersteDeadline && <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b' }}>deadline {datumKort(p.eersteDeadline)}</Typography>}
            </Box>
            <Collapse in={open}>
              <Box sx={{ p: 1.5 }}>
                <ProjectPlanning rij={p} disciplines={disciplines} onGewijzigd={onGewijzigd} />
              </Box>
            </Collapse>
          </Box>
        );
      })}
    </Stack>
  );
}

/**
 * De deadline-check is het planningsgrid van Overzicht per project, ingebed
 * (Joris, 9 sep: kopiëren/plakken, deliverables in- en uitklappen, en op
 * projectniveau meteen zien wat er mist). Per project een telling van de
 * signalen in de kop; per deliverable de chips naast de deadline, die daar
 * direct aan te passen is.
 */
function BlokC({ rijen, onGewijzigd }) {
  const perProject = useMemo(() => Object.fromEntries(rijen.map((r) => [r.id, r])), [rijen]);
  const deliverableSignalen = useMemo(() => {
    const uit = {};
    rijen.forEach((r) => (r.deliverables || []).forEach((d) => { if (d.signalen?.length) uit[d.id] = d.signalen; }));
    return uit;
  }, [rijen]);
  const kopExtra = useCallback((p) => {
    const rij = perProject[p.id];
    if (!rij) return null;
    const telling = {};
    (rij.deliverables || []).forEach((d) => (d.signalen || []).forEach((c) => { telling[c] = (telling[c] || 0) + 1; }));
    return (
      <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" onClick={(e) => e.stopPropagation()}>
        {Object.entries(telling).map(([c, n]) => {
          const sg = SIGNAAL[c]; if (!sg) return null;
          return <Tooltip key={c} title={sg.uitleg} arrow><Chip size="small" label={`${n}× ${sg.tekst.toLowerCase()}`} sx={chipSx(sg)} /></Tooltip>;
        })}
        {(rij.waarschuwingen || []).map((w) => <Waarschuwing key={w} code={w} rij={rij} />)}
      </Stack>
    );
  }, [perProject]);
  if (!rijen.length) return <Alert severity="success" sx={{ fontFamily: FONT }}>Geen deadlines met een signaal.</Alert>;
  return <OverzichtPerProject ingebed projectIds={rijen.map((r) => r.id)} kopExtra={kopExtra} deliverableSignalen={deliverableSignalen} onGewijzigd={onGewijzigd} />;
}

export default function Inplannen() {
  const [data, setData] = useState(null);
  const [laden, setLaden] = useState(true);
  const [fout, setFout] = useState(null);
  const [pl, setPl] = useState('');
  const [tab, setTab] = useState('alle');
  const [blok, setBlok] = useState('A');
  const kortKlant = useKlantAfkortingen();

  const laad = useCallback(async (vers = false) => {
    setLaden(true); setFout(null);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/inplannen/overzicht${vers ? '?vers=true' : ''}`);
      setData(await leesJson(r, 'Nog in te plannen laden'));
    } catch (e) {
      setFout(e.message || 'Laden mislukt');
    } finally {
      setLaden(false);
    }
  }, []);
  useEffect(() => { laad(); }, [laad]);
  useEffect(() => registreerOngedaanSneltoets(), []);

  // Na een deadline-wijziging: de regel meteen bijwerken, en de signalen even
  // later vers uit de engine halen (die rekent 'past niet' en 'ruimte' opnieuw).
  const hertelTimer = useRef(null);
  // Na afvinken of een deadline-wijziging: even later vers uit de engine halen,
  // die telt uren en signalen opnieuw. Wie snel meerdere taken afvinkt, krijgt één verversing.
  const hertelLater = useCallback(() => {
    clearTimeout(hertelTimer.current);
    hertelTimer.current = setTimeout(() => laad(true), 2500);
  }, [laad]);

  const disciplines = disciplinesVoorTab(tab);
  const filterPl = useCallback((rijen) => (pl ? rijen.filter((r) => r.pl === pl) : rijen), [pl]);

  const blokA = useMemo(() => filterPl(data?.blokA || []).filter((r) => heeftOpenWerk(r, disciplines)), [data, filterPl, disciplines]);
  const blokC = useMemo(() => filterPl(data?.blokC || []), [data, filterPl]);
  const blokB = useMemo(() => filterPl(data?.blokB?.projecten || []), [data, filterPl]);
  const aantalB = blokB.reduce((s, p) => s + (p.taken || []).filter((t) => t.toestand === 'zonder_eigenaar' && disciplines.includes(t.discipline)).length, 0);

  return (
    <>
      <Box sx={{ width: '100%', maxWidth: 1800, mx: 'auto', px: { xs: 2, sm: 3 }, pt: { xs: 3, md: 5 }, pb: 6 }}>
        <PageHeader icon="pending_actions" title="Nog in te plannen"
          subtitle="Gedraaid maar nog geen montage in de planning, taken zonder eigenaar en de deadline-check voor de PL's" />

        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          <ToggleButtonGroup size="small" exclusive value={tab} onChange={(_, v) => v && setTab(v)}
            sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontFamily: FONT, fontSize: 13, px: 1.5, py: 0.4 } }}>
            <ToggleButton value="alle">Alles</ToggleButton>
            <ToggleButton value="editors">Editors</ToggleButton>
            <ToggleButton value="animators">Animators</ToggleButton>
          </ToggleButtonGroup>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Projectleider</InputLabel>
            <Select label="Projectleider" value={pl} onChange={(e) => setPl(e.target.value)} sx={{ fontFamily: FONT, fontSize: 14, bgcolor: '#fff' }}>
              <MenuItem value=""><em>Alle PL's</em></MenuItem>
              {(data?.pls || []).map((naam) => <MenuItem key={naam} value={naam} sx={{ fontSize: 13.5 }}>{naam}</MenuItem>)}
            </Select>
          </FormControl>
          <Box sx={{ flex: 1 }} />
          {data?.productmap && (
            <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b' }}>
              Norm {data.productmap.normUrenPerDag} u per werkdag · producten: {['montage', 'animatie', 'feedback'].map((d) => (data.productmap[d] || []).join('/')).join(', ')}
              {' · '}<Typography component={RouterLink} to="/admin/settings" sx={{ fontFamily: FONT, fontSize: 12, color: '#0f766e' }}>instellen</Typography>
            </Typography>
          )}
          <Tooltip title="Opnieuw berekenen"><span><IconButton size="small" onClick={() => laad(true)} disabled={laden}><RefreshIcon fontSize="small" /></IconButton></span></Tooltip>
        </Stack>

        <Tabs value={blok} onChange={(_, v) => setBlok(v)} sx={{ mb: 2, borderBottom: '1px solid #e2e8f0', '& .MuiTab-root': { textTransform: 'none', fontFamily: NAV_FONT, fontWeight: 700, fontSize: 13.5, minHeight: 40 } }}>
          <Tab value="A" label={`Gedraaid, montage nog niet (${blokA.length})`} />
          <Tab value="B" label={`Zonder eigenaar (${aantalB})`} />
          <Tab value="C" label={`Deadline-check (${blokC.length})`} />
        </Tabs>

        {fout && <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={() => laad(true)}>Opnieuw</Button>}>{fout}</Alert>}
        {laden && !data ? <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress size={28} /></Box> : (
          <>
            {blok === 'A' && (
              <>
                <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: '#64748b', mb: 1.25 }}>
                  Lopende projecten (geen Strippenkaart) met minstens één draaidag waarvan de postproductie niet volledig is ingepland.
                  Gepland = eigenaar én datum; zonder datum telt half. Klik een rij open voor de planning en wie wat heeft; beweeg over de uren voor het lijstje.
                </Typography>
                <BlokA rijen={blokA} disciplines={disciplines} kortKlant={kortKlant} onGewijzigd={hertelLater} />
              </>
            )}
            {blok === 'B' && (
              <>
                <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: '#64748b', mb: 1.25 }}>
                  Projecten met montage-, animatie- of feedbacktaken zonder eigenaar, met hun hele planning erbij: gestreept = nog geen datum. Slepen op een persoon doe je in Planner To-Do.
                  {data?.blokB?.totaal && ` Totaal zonder eigenaar: montage ${uur(data.blokB.totaal.montage)} u · animatie ${uur(data.blokB.totaal.animatie)} u · feedback ${uur(data.blokB.totaal.feedback)} u.`}
                </Typography>
                <BlokB projecten={blokB} disciplines={disciplines} kortKlant={kortKlant} onGewijzigd={hertelLater} />
              </>
            )}
            {blok === 'C' && (
              <>
                <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: '#64748b', mb: 1.25 }}>
                  Projecten met een deliverable-signaal, in het planningsgrid: klap een project open voor de deliverables en taken, pas de deadline aan in de kop van de deliverable, kopieer en plak zoals in een spreadsheet.
                </Typography>
                <BlokC rijen={blokC} onGewijzigd={hertelLater} />
              </>
            )}
          </>
        )}
      </Box>
    </>
  );
}
